import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Calendar, Check, Clock } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ConsultationsPage = () => {
  const [formData, setFormData] = useState({
    service_type: 'nutrition_consultation',
    brand: 'nutrition',
    date: '',
    time_slot: '',
    name: '',
    email: '',
    phone: '',
    notes: ''
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [bookingId, setBookingId] = useState('');

  const timeSlots = [
    '09:00 AM', '10:00 AM', '11:00 AM', '12:00 PM',
    '02:00 PM', '03:00 PM', '04:00 PM', '05:00 PM'
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/bookings`, formData);
      setBookingId(response.data.id);
      setSubmitted(true);
      toast.success('Consultation booked successfully!');
    } catch (error) {
      toast.error('Failed to book consultation');
    } finally {
      setLoading(false);
    }
  };

  // Get min date (tomorrow)
  const minDate = new Date();
  minDate.setDate(minDate.getDate() + 1);
  const minDateStr = minDate.toISOString().split('T')[0];

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] pt-20 flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center px-6 max-w-lg"
        >
          <div className="w-20 h-20 bg-[#E5D3B3]/10 rounded-full flex items-center justify-center mx-auto mb-8">
            <Check className="w-10 h-10 text-[#E5D3B3]" />
          </div>
          <h1 className="font-serif text-4xl text-[#F5F2EB] mb-4">Consultation Booked!</h1>
          <p className="text-[#A3988F] mb-4">
            Your consultation has been confirmed. A confirmation email has been sent to your inbox.
          </p>
          <div className="luxury-card rounded-xl p-6 mb-8">
            <p className="text-sm text-[#A3988F]">Booking ID</p>
            <p className="text-[#E5D3B3] font-mono" data-testid="booking-id">{bookingId.slice(0, 8)}</p>
          </div>
          <Link to="/nutrition" className="btn-primary inline-flex items-center space-x-2" data-testid="consultation-back-btn">
            <span>Back to Nutrition</span>
            <ArrowRight className="w-4 h-4" />
          </Link>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] pt-20">
      {/* Hero */}
      <section className="section-padding pb-12">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center max-w-3xl mx-auto"
          >
            <p className="text-sm tracking-[0.3em] uppercase text-[#E5D3B3] mb-4">Consultations</p>
            <h1 className="font-serif text-5xl md:text-6xl text-[#F5F2EB] mb-6">Book a Consultation</h1>
            <p className="text-lg text-[#A3988F]">
              Schedule a one-on-one session with our certified nutritionists and dieticians.
            </p>
          </motion.div>
        </div>
      </section>

      {/* What to Expect */}
      <section className="section-padding pt-0">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8 mb-16">
            {[
              { icon: Calendar, title: 'Flexible Scheduling', desc: 'Choose a time that works best for you' },
              { icon: Clock, title: '60-Minute Session', desc: 'Comprehensive consultation with an expert' },
              { icon: Check, title: 'Personalized Advice', desc: 'Tailored recommendations for your goals' }
            ].map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center"
              >
                <div className="w-16 h-16 bg-[#E5D3B3]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <item.icon className="w-7 h-7 text-[#E5D3B3]" />
                </div>
                <h3 className="font-serif text-xl text-[#F5F2EB] mb-2">{item.title}</h3>
                <p className="text-[#A3988F]">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Booking Form */}
      <section className="section-padding pt-0 bg-[#1A1514]">
        <div className="max-w-3xl mx-auto py-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-serif text-4xl text-[#F5F2EB]">Schedule Your Session</h2>
          </motion.div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm text-[#A3988F] mb-2">Consultation Type</label>
                <select
                  value={formData.service_type}
                  onChange={(e) => setFormData({...formData, service_type: e.target.value})}
                  className="luxury-select"
                  data-testid="consultation-type-select"
                >
                  <option value="nutrition_consultation">General Nutrition Consultation</option>
                  <option value="diet_planning">Diet Planning Session</option>
                  <option value="health_assessment">Health Assessment</option>
                </select>
              </div>
              <div>
                <label className="block text-sm text-[#A3988F] mb-2">Preferred Date</label>
                <input
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({...formData, date: e.target.value})}
                  min={minDateStr}
                  className="luxury-input"
                  required
                  data-testid="consultation-date-input"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm text-[#A3988F] mb-2">Preferred Time</label>
              <div className="grid grid-cols-4 gap-3">
                {timeSlots.map((slot) => (
                  <button
                    key={slot}
                    type="button"
                    onClick={() => setFormData({...formData, time_slot: slot})}
                    className={`p-3 rounded-lg text-sm transition-all ${
                      formData.time_slot === slot
                        ? 'bg-[#E5D3B3] text-[#0A0A0A]'
                        : 'bg-[#261E1D] text-[#A3988F] hover:bg-[#E5D3B3]/10'
                    }`}
                    data-testid={`time-slot-${slot.replace(/[: ]/g, '-')}`}
                  >
                    {slot}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm text-[#A3988F] mb-2">Full Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="luxury-input"
                  required
                  data-testid="consultation-name-input"
                />
              </div>
              <div>
                <label className="block text-sm text-[#A3988F] mb-2">Email</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  className="luxury-input"
                  required
                  data-testid="consultation-email-input"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm text-[#A3988F] mb-2">Phone</label>
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({...formData, phone: e.target.value})}
                className="luxury-input"
                required
                data-testid="consultation-phone-input"
              />
            </div>

            <div>
              <label className="block text-sm text-[#A3988F] mb-2">Additional Notes (Optional)</label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                className="luxury-input h-32 resize-none"
                placeholder="Any specific concerns or goals you'd like to discuss..."
                data-testid="consultation-notes-input"
              />
            </div>

            <button
              type="submit"
              disabled={loading || !formData.time_slot}
              className="btn-primary w-full flex items-center justify-center space-x-2"
              data-testid="consultation-submit-btn"
            >
              {loading ? (
                <div className="loader w-5 h-5" />
              ) : (
                <>
                  <Calendar className="w-4 h-4" />
                  <span>Book Consultation</span>
                </>
              )}
            </button>
          </form>
        </div>
      </section>
    </div>
  );
};

export default ConsultationsPage;
